import { mutationOptions, useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { useFlyoutDispatch } from 'src/hooks'
import {
  AttachmentConnectedPlatformListResponse,
  ReceiptAttachmentResponse,
} from 'src/models/receiptAttachment.models'
import { QueryError } from 'src/queries/network.utils'
import {
  createPostERPReceiptAttachmentsFailureAction,
  createPostERPReceiptAttachmentsInitAction,
  createPostERPReceiptAttachmentsSuccessAction,
  createUploadAttachmentFailureAction,
  createUploadAttachmentInitAction,
  createUploadAttachmentSuccessAction,
  UploadAttachmentFailureAction,
  UploadAttachmentSuccessAction,
} from 'src/queries/receiptAttachments/receiptAttachments.events'
import {
  postERPReceiptAttachments,
  PostERPReceiptAttachmentsParams,
  postReceiptAttachment,
} from 'src/queries/receiptAttachments/receiptAttachments.network'
import { RootState } from 'src/redux/createRootReducer'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'

type AppDispatch = ThunkDispatch<RootState, void, AnyAction>

export const receiptAttachmentsMutationKeys = {
  all: () => ['receiptAttachments'] as const,
  upload: () => [...receiptAttachmentsMutationKeys.all(), 'upload'] as const,
  pushToERP: () =>
    [...receiptAttachmentsMutationKeys.all(), 'pushToERP'] as const,
}

export type UploadReceiptAttachmentsResult = Array<
  UploadAttachmentFailureAction | UploadAttachmentSuccessAction
>

export const uploadReceiptAttachmentWithDispatch = async (
  attachment: PostReceiptAttachmentRequest,
  dispatch: AppDispatch,
): Promise<UploadAttachmentFailureAction | UploadAttachmentSuccessAction> => {
  dispatch(
    createUploadAttachmentInitAction({
      uploadId: attachment.uploadId,
      thumbnailUrl: attachment.thumbnailUrl,
      fileName: attachment.fileName,
    }),
  )

  try {
    const response = await postReceiptAttachment(attachment.formData)
    return dispatch(
      createUploadAttachmentSuccessAction({
        ...response,
        uploadId: attachment.uploadId,
      }),
    )
  } catch {
    return dispatch(
      createUploadAttachmentFailureAction({
        uploadId: attachment.uploadId,
      }),
    )
  }
}

export const receiptAttachmentsMutations = {
  upload: (dispatch: AppDispatch) =>
    mutationOptions<
      UploadReceiptAttachmentsResult,
      QueryError,
      PostReceiptAttachmentRequest[]
    >({
      mutationKey: receiptAttachmentsMutationKeys.upload(),
      mutationFn: (attachments) =>
        Promise.all(
          attachments.map((attachment) =>
            uploadReceiptAttachmentWithDispatch(attachment, dispatch),
          ),
        ),
    }),
  pushToERP: (dispatch: AppDispatch) =>
    mutationOptions<
      AttachmentConnectedPlatformListResponse[],
      QueryError,
      PostERPReceiptAttachmentsParams
    >({
      mutationKey: receiptAttachmentsMutationKeys.pushToERP(),
      mutationFn: async (params) => {
        dispatch(createPostERPReceiptAttachmentsInitAction())
        try {
          const response = await postERPReceiptAttachments(params)
          dispatch(
            createPostERPReceiptAttachmentsSuccessAction({
              transactionId: params.transactionId,
              receiptAttachments: response,
            }),
          )
          return response
        } catch (error) {
          dispatch(
            createPostERPReceiptAttachmentsFailureAction({
              transactionId: params.transactionId,
              errors: error as QueryError,
            }),
          )
          throw error
        }
      },
    }),
}

type UseReceiptAttachmentDispatchOptions = {
  dispatchMode?: 'flyout' | 'redux'
}

const useReceiptAttachmentDispatch = ({
  dispatchMode = 'flyout',
}: UseReceiptAttachmentDispatchOptions = {}): AppDispatch => {
  const flyoutDispatch = useFlyoutDispatch()
  const reduxDispatch = useDispatch<AppDispatch>()
  return dispatchMode === 'flyout' ? flyoutDispatch : reduxDispatch
}

export const useUploadReceiptAttachments = (
  options?: UseReceiptAttachmentDispatchOptions,
) => {
  const dispatch = useReceiptAttachmentDispatch(options)
  return useMutation(receiptAttachmentsMutations.upload(dispatch))
}

export const usePushERPReceiptAttachments = (
  options?: UseReceiptAttachmentDispatchOptions,
) => {
  const dispatch = useReceiptAttachmentDispatch(options)
  return useMutation(receiptAttachmentsMutations.pushToERP(dispatch))
}

export type UploadedReceiptAttachmentResponse = ReceiptAttachmentResponse
